/**
 * NavRail — the fixed 64px domain rail (design_handoff_navigation_shell §1).
 * Nine domains from config/nav.ts, active state derived from the route. Two hover
 * behaviours behind one config value: RAIL_MODE 'expand' widens the rail to 232px
 * over the content (the page never reflows — the grid track stays 64px), while
 * 'tooltips' keeps it 64px and shows a dark tooltip beside the hovered item.
 * Colour is an active-state signal only; inactive icons stay grey.
 * A `planned` domain is dim: the link still works, but the rail shows that the
 * screen is a future enhancement.
 */

import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import clsx from 'clsx'
import Sym from './Sym'
import RailTheme from './RailTheme'
import AccountMenu from './AccountMenu'
import { NAV_ITEMS, isNavActive, type NavItem } from '../config/nav'
import { PRODUCT_NAME, RAIL_CLOSED, RAIL_MODE, RAIL_OPEN } from '../config/shell'

interface Tip {
  label: string
  top: number
}

/** Labels stay in the DOM (clipped by the rail) so they fade in rather than pop. */
function revealed(open: boolean): React.CSSProperties {
  return { opacity: open ? 1 : 0, transition: 'opacity .14s .05s' }
}

function Brand({ open }: { open: boolean }): React.JSX.Element {
  return (
    <div className="flex h-[60px] w-full shrink-0 items-center gap-[11px] overflow-hidden border-b border-line2 px-[15px]">
      <div
        className="flex h-[30px] w-[30px] min-w-[30px] items-center justify-center rounded-[9px] text-white"
        style={{ background: 'linear-gradient(180deg,#6B7FFB,#4453E8)' }}
      >
        <Sym name="neurology" size={17} weight={400} fill={1} />
      </div>
      <span className="whitespace-nowrap text-[15.5px] font-bold tracking-[-.02em] text-ink" style={revealed(open)}>
        {PRODUCT_NAME}
      </span>
    </div>
  )
}

/** Appearance and identity live here, not in the top bar. */
function RailFooter({ open, onMenuChange }: { open: boolean; onMenuChange: (v: boolean) => void }): React.JSX.Element {
  return (
    <div className="mt-auto flex w-full flex-col gap-2 overflow-hidden border-t border-line2 px-[11px] pb-3 pt-3">
      <RailTheme open={open} />
      <AccountMenu open={open} onMenuChange={onMenuChange} />
    </div>
  )
}

interface ItemProps {
  item: NavItem
  active: boolean
  open: boolean
  onTip: (tip: Tip | null) => void
}

/** `w-0` when collapsed: the label stays mounted so it can fade, but a nowrap label
 * that still claims width shoves the icon out of the centred, clipped 42px box. */
const labelClass = (open: boolean, active: boolean): string =>
  clsx(
    'overflow-hidden whitespace-nowrap text-[13.5px]',
    open ? 'w-auto' : 'w-0',
    active ? 'font-semibold text-ink' : 'font-medium text-mut'
  )

function RailItem({ item, active, open, onTip }: ItemProps): React.JSX.Element {
  const dim = Boolean(item.planned) && !active
  const enter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const box = e.currentTarget.getBoundingClientRect()
    onTip({ label: dim ? `${item.label} · future` : item.label, top: box.top + box.height / 2 })
  }
  return (
    <Link
      to={item.path}
      aria-label={dim ? `${item.label} — future enhancement` : item.label}
      aria-current={active ? 'page' : undefined}
      onMouseEnter={enter}
      onMouseLeave={() => onTip(null)}
      className={clsx(
        'cx-navi relative flex h-[42px] items-center overflow-hidden rounded-[11px]',
        open ? 'justify-start gap-[11px] pl-[11px]' : 'justify-center gap-0',
        dim && 'opacity-40'
      )}
      style={{ background: active ? `color-mix(in srgb, ${item.color} 8%, transparent)` : 'transparent' }}
    >
      <Sym
        name={item.icon}
        weight={active ? 400 : 300}
        className="min-w-[21px]"
        style={{ color: active ? item.color : 'var(--mut)' }}
      />
      <span className={labelClass(open, active)} style={revealed(open)}>
        {item.label}
      </span>
      {active && (
        <span className="absolute left-[-11px] top-[11px] h-5 w-[3px] rounded-r-[3px]" style={{ background: item.color }} />
      )}
    </Link>
  )
}

function Items({ open, onTip }: { open: boolean; onTip: (tip: Tip | null) => void }): React.JSX.Element {
  const { pathname } = useLocation()
  return (
    <div className="flex w-full flex-col gap-1.5 px-[11px] pt-3">
      {NAV_ITEMS.map((item) => (
        <RailItem key={item.id} item={item} active={isNavActive(pathname, item)} open={open} onTip={onTip} />
      ))}
    </div>
  )
}

/** Decorative duplicate of the item's accessible name — never the only label source. */
function Tooltip({ tip }: { tip: Tip }): React.JSX.Element {
  return (
    <div
      role="tooltip"
      className="pointer-events-none fixed z-[60] -translate-y-1/2 whitespace-nowrap rounded-lg px-[11px] py-1.5 text-[12.5px] font-medium text-white"
      style={{ left: 74, top: tip.top, background: '#171A1F', boxShadow: '0 6px 20px rgba(15,20,30,.18)' }}
    >
      {tip.label}
    </div>
  )
}

export default function NavRail(): React.JSX.Element {
  const [open, setOpen] = useState(false)
  const [pinned, setPinned] = useState(false) // the account menu holds the rail open
  const [tip, setTip] = useState<Tip | null>(null)
  const expandMode = RAIL_MODE === 'expand'
  const isOpen = expandMode && (open || pinned)

  // Expand on focus too, or the labels are mouse-only (handoff, accessibility).
  const expand = (next: boolean) => {
    if (!expandMode) return
    setOpen(next)
    if (next) setTip(null)
  }

  return (
    <>
      <nav
        aria-label="Domains"
        onMouseEnter={() => expand(true)}
        onMouseLeave={() => expand(false)}
        onFocus={() => expand(true)}
        onBlur={() => expand(false)}
        className="print:hidden fixed left-0 top-0 z-40 flex h-screen flex-col items-start overflow-hidden border-r border-line bg-panel"
        style={{
          width: isOpen ? RAIL_OPEN : RAIL_CLOSED,
          transition: 'width .18s cubic-bezier(.4,0,.2,1), box-shadow .18s',
          boxShadow: isOpen ? '6px 0 28px rgba(15,20,30,.10)' : 'none',
        }}
      >
        <Brand open={isOpen} />
        <Items open={isOpen} onTip={(next) => setTip(expandMode ? null : next)} />
        <RailFooter open={isOpen} onMenuChange={setPinned} />
      </nav>
      {tip && <Tooltip tip={tip} />}
    </>
  )
}
